import { addDays, todayIn, weekdayIndex } from './date.js';
import { buildRule, nextOccurrences } from './recurrence.js';
const DAYS = {
    mon: 0, monday: 0, tue: 1, tues: 1, tuesday: 1, wed: 2, weds: 2, wednesday: 2,
    thu: 3, thur: 3, thurs: 3, thursday: 3, fri: 4, friday: 4,
    sat: 5, saturday: 5, sun: 6, sunday: 6,
};
const MONTHS = {
    jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
    may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9,
    september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
};
const pad = (n) => String(n).padStart(2, '0');
function normalizeTime(h, m, mer) {
    let hh = h;
    if (mer === 'pm' && hh < 12)
        hh += 12;
    if (mer === 'am' && hh === 12)
        hh = 0;
    if (hh > 23 || m > 59)
        return null;
    return `${pad(hh)}:${pad(m)}`;
}
/** Removes each matched span from the text so the remainder becomes the title. */
class Cutter {
    text;
    cuts = [];
    constructor(text) {
        this.text = text;
    }
    overlaps(start, end) {
        return this.cuts.some(([s, e]) => start < e && end > s);
    }
    /** `keep` lets a caller inspect a match and decline to remove it — a bare "for"
     *  with no duration after it is part of the title, not a duration.
     *
     *  Matches that overlap something already claimed are skipped, not returned.
     *  Without this, "every mon & thu" hands its "mon" to the weekday-date matcher
     *  as well as the recurrence one, and the task silently starts a week late. */
    match(re, keep) {
        const g = new RegExp(re.source, re.flags.includes('g') ? re.flags : `${re.flags}g`);
        for (const m of this.text.matchAll(g)) {
            const start = m.index, end = start + m[0].length;
            if (this.overlaps(start, end))
                continue;
            if (keep && !keep(m))
                return m;
            this.cuts.push([start, end]);
            return m;
        }
        return null;
    }
    /** Every match, all removed from the title. */
    matchAll(re) {
        const out = [];
        const g = new RegExp(re.source, re.flags.includes('g') ? re.flags : `${re.flags}g`);
        for (const m of this.text.matchAll(g)) {
            const start = m.index, end = start + m[0].length;
            if (this.overlaps(start, end))
                continue;
            out.push(m);
            this.cuts.push([start, end]);
        }
        return out;
    }
    rest() {
        let out = '';
        let i = 0;
        for (const [s, e] of this.cuts.sort((a, b) => a[0] - b[0])) {
            if (s >= i) {
                out += this.text.slice(i, s);
                i = e;
            }
            else if (e > i)
                i = e;
        }
        out += this.text.slice(i);
        return out.replace(/\s{2,}/g, ' ').replace(/^[\s,;–-]+|[\s,;–-]+$/g, '').trim();
    }
}
export function parseQuickAdd(input, opts = {}) {
    const today = opts.today ?? todayIn(opts.tz ?? 'UTC');
    const c = new Cutter(input);
    // @assignee and #list
    const at = c.match(/(?:^|\s)@([a-z0-9_-]+)/i);
    const hashes = c.matchAll(/(?:^|\s)#([a-z0-9_-]+)/i);
    // duration: "for 30m", "for 1h", "for 1h30"
    let durationMin = null;
    const dur = c.match(/\bfor\s+(?:(\d+)\s*h(?:ours?)?)?\s*(?:(\d+)\s*m(?:in(?:utes?)?)?)?\b/i, (m) => Boolean(m[1] || m[2]));
    if (dur && (dur[1] || dur[2]))
        durationMin = (Number(dur[1] ?? 0) * 60) + Number(dur[2] ?? 0);
    // time: "at 19:30", "at 7pm", "19:30", "7.30pm"
    let startTime = null;
    const t = c.match(/(?:\bat\s+)?\b(\d{1,2})[:.](\d{2})\s*(am|pm)?\b|\bat\s+(\d{1,2})\s*(am|pm)\b/i);
    if (t) {
        startTime = t[1]
            ? normalizeTime(Number(t[1]), Number(t[2]), t[3]?.toLowerCase())
            : normalizeTime(Number(t[4]), 0, t[5]?.toLowerCase());
    }
    // recurrence
    let rrule = null;
    const every = c.match(/\b(?:every|each)\s+(day|week|month|year|weekday|weekdays|weekend|weekends|other\s+\w+|\d+\s+\w+|[a-z]{3,9}(?:\s*(?:,|&|and)\s*[a-z]{3,9})*)\b/i);
    const bare = every ? null : c.match(/\b(daily|weekly|monthly|yearly|annually|weekdays)\b/i);
    if (every) {
        const raw = every[1].toLowerCase().trim();
        if (/^weekdays?$/.test(raw))
            rrule = buildRule({ freq: 'weekly', byWeekday: [0, 1, 2, 3, 4] });
        else if (/^weekends?$/.test(raw))
            rrule = buildRule({ freq: 'weekly', byWeekday: [5, 6] });
        else if (raw === 'day')
            rrule = buildRule({ freq: 'daily' });
        else if (raw === 'week')
            rrule = buildRule({ freq: 'weekly' });
        else if (raw === 'month')
            rrule = buildRule({ freq: 'monthly' });
        else if (raw === 'year')
            rrule = buildRule({ freq: 'yearly' });
        else {
            const nth = /^(?:other|(\d+))\s+(day|week|month|year)s?$/.exec(raw);
            if (nth) {
                const interval = nth[1] ? Number(nth[1]) : 2;
                rrule = buildRule({ freq: `${nth[2]}ly`, interval });
            }
            else {
                const names = raw.split(/\s*(?:,|&|and)\s*/).map((s) => s.trim());
                const idx = names.map((n) => DAYS[n]).filter((n) => n !== undefined);
                if (idx.length)
                    rrule = buildRule({ freq: 'weekly', byWeekday: [...new Set(idx)].sort((a, b) => a - b) });
            }
        }
    }
    else if (bare) {
        const w = bare[1].toLowerCase();
        if (w === 'weekdays')
            rrule = buildRule({ freq: 'weekly', byWeekday: [0, 1, 2, 3, 4] });
        else
            rrule = buildRule({ freq: w === 'annually' ? 'yearly' : (w.replace(/ly$/, '') === 'dai' ? 'daily' : w) });
    }
    // date
    let startDate = null;
    if (c.match(/\btoday\b/i))
        startDate = today;
    else if (c.match(/\btomorrow\b/i))
        startDate = addDays(today, 1);
    else {
        const nextDay = c.match(/\bnext\s+(mon|tue|tues|wed|weds|thu|thur|thurs|fri|sat|sun)[a-z]*\b/i);
        const onDay = nextDay ? null : c.match(/\b(?:on\s+)?(mon|tue|tues|wed|weds|thu|thur|thurs|fri|sat|sun)(?:day|s|sday|nesday|rsday|urday)?\b/i);
        const dm = (nextDay || onDay) ? null : c.match(/\b(?:on\s+)?(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\b/i);
        const iso = (nextDay || onDay || dm) ? null : c.match(/\b(?:on\s+)?(\d{4}-\d{2}-\d{2})\b/);
        if (nextDay || onDay) {
            const key = (nextDay ?? onDay)[1].toLowerCase();
            const target = DAYS[key];
            const cur = weekdayIndex(today);
            let delta = (target - cur + 7) % 7;
            if (delta === 0 || nextDay)
                delta = delta === 0 ? 7 : delta;
            startDate = addDays(today, delta);
        }
        else if (dm) {
            const day = Number(dm[1]);
            const month = MONTHS[dm[2].toLowerCase()];
            const year = Number(today.slice(0, 4));
            const cand = `${year}-${pad(month)}-${pad(day)}`;
            startDate = cand >= today ? cand : `${year + 1}-${pad(month)}-${pad(day)}`;
        }
        else if (iso) {
            startDate = iso[1];
        }
    }
    // A repeating task with no explicit date is anchored to the first day the rule
    // actually produces, not to today. Otherwise "every tuesday" typed on a Sunday
    // starts on the Sunday — the expansion is still right, but the task then reads
    // "Every week on Tuesday, since Sunday", which is nonsense to a person.
    let start = startDate ?? today;
    if (!startDate && rrule)
        start = nextOccurrences(today, rrule, today, 1)[0] ?? today;
    return {
        title: c.rest() || input.trim(),
        startDate: start,
        startTime,
        durationMin,
        rrule,
        assigneeHint: at?.[1]?.toLowerCase() ?? null,
        tagHints: [...new Set(hashes.map((m) => m[1].toLowerCase()))],
        // A rule with no date of its own still pins a day — the first one it
        // produces — and that counts as the sentence having named it.
        matchedDate: startDate !== null || rrule !== null,
    };
}
//# sourceMappingURL=quickadd.js.map